import { useState, useEffect, useContext } from "react";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { 
  Newspaper, TrendingUp, Clock, Plus, Search, 
  ThumbsUp, ThumbsDown, Filter, Sparkles, Calendar,
  Link2, Loader2, Zap
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

export const NewsBettingPage = () => {
  const { user, refreshBalance, token } = useContext(UserContext);
  const [markets, setMarkets] = useState([]);
  const [myBets, setMyBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [betAmount, setBetAmount] = useState(100);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [articleUrl, setArticleUrl] = useState("");
  const [creatingFromArticle, setCreatingFromArticle] = useState(false);
  
  // Create market form
  const [newMarket, setNewMarket] = useState({
    title: "",
    description: "",
    category: "General",
    end_date: ""
  });

  const categories = ["all", "Crypto", "Sports", "Tech", "Business", "Science"];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [marketsRes, betsRes] = await Promise.all([
        axios.get(`${API}/news/markets`),
        axios.get(`${API}/news/my-bets`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { bets: [] } }))
      ]);
      setMarkets(marketsRes.data.markets || []);
      setMyBets(betsRes.data.bets || []);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const placeBet = async () => {
    if (!selectedMarket || !selectedPosition || betAmount <= 0) {
      toast.error("Please select a market and position");
      return;
    }

    try {
      await axios.post(`${API}/news/bet`, {
        market_id: selectedMarket.market_id,
        position: selectedPosition,
        amount: betAmount
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Bet placed on ${selectedPosition.toUpperCase()}`);
      setSelectedMarket(null);
      setSelectedPosition(null);
      await refreshBalance();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to place bet");
    }
  };

  const createMarket = async () => {
    if (!newMarket.title || !newMarket.end_date) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      await axios.post(`${API}/news/markets/create`, {
        ...newMarket,
        end_date: new Date(newMarket.end_date).toISOString()
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Market created!");
      setShowCreateModal(false);
      setNewMarket({ title: "", description: "", category: "General", end_date: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create market");
    }
  };

  const createFromArticle = async () => {
    if (!articleUrl || !articleUrl.startsWith("http")) {
      toast.error("Please enter a valid article URL");
      return;
    }

    setCreatingFromArticle(true);
    try {
      const response = await axios.post(`${API}/ai/bet-on-article`, {
        article_url: articleUrl
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Market created from article!");
      setShowArticleModal(false);
      setArticleUrl("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create market from article");
    } finally {
      setCreatingFromArticle(false);
    }
  };

  const filteredMarkets = markets.filter(m => {
    const matchesSearch = m.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || m.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getOdds = (market) => {
    const total = (market.yes_pool || 0) + (market.no_pool || 0);
    if (total === 0) return { yes: 50, no: 50 };
    return {
      yes: Math.round((market.yes_pool / total) * 100),
      no: Math.round((market.no_pool / total) * 100)
    };
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#00E701]/20 flex items-center justify-center">
              <Newspaper className="w-6 h-6 text-[#00E701]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white font-['Rajdhani']" data-testid="news-betting-title">
                News Betting
              </h1>
              <p className="text-[#A1A1AA] text-sm">Bet on real-world predictions</p>
            </div>
          </div>
          
          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button className="bg-[#00E701] hover:bg-[#00C201] text-black font-bold" data-testid="create-market-btn">
                <Plus className="w-4 h-4 mr-2" /> Create Market
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1A1B1F] border-white/[0.08] text-white">
              <DialogHeader>
                <DialogTitle className="font-['Rajdhani'] text-xl">Create New Market</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Question (Yes/No format)</label>
                  <Input
                    value={newMarket.title}
                    onChange={(e) => setNewMarket({ ...newMarket, title: e.target.value })}
                    placeholder="Will Bitcoin reach $100k by..."
                    className="bg-black/20 border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Description</label>
                  <Input
                    value={newMarket.description}
                    onChange={(e) => setNewMarket({ ...newMarket, description: e.target.value })}
                    placeholder="Resolution criteria..."
                    className="bg-black/20 border-white/10 text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[#A1A1AA] text-sm mb-2 block">Category</label>
                    <select
                      value={newMarket.category}
                      onChange={(e) => setNewMarket({ ...newMarket, category: e.target.value })}
                      className="w-full h-10 rounded-md bg-black/20 border border-white/10 text-white px-3"
                    >
                      {categories.filter(c => c !== "all").map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[#A1A1AA] text-sm mb-2 block">End Date</label>
                    <Input
                      type="datetime-local"
                      value={newMarket.end_date}
                      onChange={(e) => setNewMarket({ ...newMarket, end_date: e.target.value })}
                      className="bg-black/20 border-white/10 text-white"
                    />
                  </div>
                </div>
                <Button onClick={createMarket} className="w-full bg-[#00E701] hover:bg-[#00C201] text-black font-bold">
                  Create Market
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Bet on Article Button */}
          <Dialog open={showArticleModal} onOpenChange={setShowArticleModal}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                className="border-[#FFD600] text-[#FFD600] hover:bg-[#FFD600]/10"
                data-testid="bet-on-article-btn"
              >
                <Link2 className="w-4 h-4 mr-2" /> Bet on Article
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1A1B1F] border-white/[0.08] text-white">
              <DialogHeader>
                <DialogTitle className="font-['Rajdhani'] text-xl flex items-center gap-2">
                  <Zap className="w-5 h-5 text-[#FFD600]" />
                  AI-Powered Bet Creation
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <p className="text-[#A1A1AA] text-sm">
                  Paste any news article URL and our AI (Claude) will automatically create a betting market based on it!
                </p>
                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Article URL</label>
                  <Input
                    value={articleUrl}
                    onChange={(e) => setArticleUrl(e.target.value)}
                    placeholder="https://news.example.com/article..."
                    className="bg-black/20 border-white/10 text-white"
                    data-testid="article-url-input"
                  />
                </div>
                <div className="p-3 rounded-lg bg-[#FFD600]/10 border border-[#FFD600]/20">
                  <p className="text-[#FFD600] text-sm flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Claude AI will analyze the article and generate a prediction market
                  </p>
                </div>
                <Button 
                  onClick={createFromArticle} 
                  disabled={creatingFromArticle || !articleUrl}
                  className="w-full bg-[#FFD600] hover:bg-[#E6C200] text-black font-bold"
                  data-testid="create-from-article-btn"
                >
                  {creatingFromArticle ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating Market...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Create Market from Article
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#52525B]" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search markets..."
              className="pl-10 h-12 bg-[#1A1B1F] border-white/10 text-white"
              data-testid="market-search-input"
            />
          </div>
          
          <div className="flex gap-2 overflow-x-auto pb-2">
            {categories.map(cat => (
              <Button
                key={cat}
                variant={selectedCategory === cat ? "default" : "outline"}
                onClick={() => setSelectedCategory(cat)}
                className={`capitalize whitespace-nowrap ${
                  selectedCategory === cat 
                    ? 'bg-[#00E701] text-black hover:bg-[#00C201]' 
                    : 'border-white/10 text-white/60 hover:text-white hover:bg-white/5'
                }`}
              >
                {cat}
              </Button>
            ))}
          </div>
        </div>

        <Tabs defaultValue="markets" className="space-y-6">
          <TabsList className="bg-[#1A1B1F] border border-white/[0.08]">
            <TabsTrigger value="markets" className="data-[state=active]:bg-[#00E701] data-[state=active]:text-black">
              Markets
            </TabsTrigger>
            <TabsTrigger value="my-bets" className="data-[state=active]:bg-[#00E701] data-[state=active]:text-black">
              My Bets
            </TabsTrigger>
          </TabsList>

          <TabsContent value="markets">
            {loading ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 border-[#00E701] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-[#A1A1AA]">Loading markets...</p>
              </div>
            ) : filteredMarkets.length === 0 ? (
              <div className="text-center py-12">
                <Newspaper className="w-12 h-12 text-[#52525B] mx-auto mb-4" />
                <p className="text-[#A1A1AA]">No markets found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredMarkets.map(market => {
                  const odds = getOdds(market);
                  return (
                    <div
                      key={market.market_id}
                      className="p-6 rounded-xl bg-[#1A1B1F] border border-white/[0.08] hover:border-white/20 transition-colors"
                      data-testid={`market-card-${market.market_id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-1 rounded text-xs font-medium bg-[#00E701]/10 text-[#00E701]">
                              {market.category}
                            </span>
                            <span className="flex items-center gap-1 text-[#52525B] text-xs">
                              <Clock className="w-3 h-3" />
                              Ends {new Date(market.end_date).toLocaleDateString()}
                            </span>
                          </div>
                          
                          <h3 className="text-lg font-bold text-white mb-2">{market.title}</h3>
                          <p className="text-[#A1A1AA] text-sm mb-4">{market.description}</p>
                          
                          {/* Odds Bar */}
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-[#00E701]">Yes {odds.yes}%</span>
                              <span className="text-[#FF3B30]">No {odds.no}%</span>
                            </div>
                            <div className="h-3 rounded-full bg-[#FF3B30] overflow-hidden">
                              <div 
                                className="h-full bg-[#00E701] transition-all"
                                style={{ width: `${odds.yes}%` }}
                              />
                            </div>
                            <div className="flex justify-between text-xs text-[#52525B]">
                              <span>₹{(market.yes_pool || 0).toLocaleString()}</span>
                              <span>₹{(market.no_pool || 0).toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Bet Buttons */}
                        <div className="flex md:flex-col gap-2">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button 
                                onClick={() => { setSelectedMarket(market); setSelectedPosition('yes'); }}
                                className="flex-1 md:w-32 bg-[#00E701] hover:bg-[#00C201] text-black font-bold"
                              >
                                <ThumbsUp className="w-4 h-4 mr-2" /> Yes
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-[#1A1B1F] border-white/[0.08] text-white">
                              <DialogHeader>
                                <DialogTitle>Place Bet - YES</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4 mt-4">
                                <p className="text-[#A1A1AA]">{market.title}</p>
                                <div>
                                  <label className="text-sm text-[#A1A1AA] mb-2 block">Amount (₹)</label>
                                  <Input
                                    type="number"
                                    value={betAmount}
                                    onChange={(e) => setBetAmount(Number(e.target.value))}
                                    className="bg-black/20 border-white/10 text-white"
                                  />
                                </div>
                                <Button onClick={placeBet} className="w-full bg-[#00E701] hover:bg-[#00C201] text-black font-bold">
                                  Confirm Bet
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                          
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button 
                                onClick={() => { setSelectedMarket(market); setSelectedPosition('no'); }}
                                variant="outline"
                                className="flex-1 md:w-32 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10"
                              >
                                <ThumbsDown className="w-4 h-4 mr-2" /> No
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-[#1A1B1F] border-white/[0.08] text-white">
                              <DialogHeader>
                                <DialogTitle>Place Bet - NO</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4 mt-4">
                                <p className="text-[#A1A1AA]">{market.title}</p>
                                <div>
                                  <label className="text-sm text-[#A1A1AA] mb-2 block">Amount (₹)</label>
                                  <Input
                                    type="number"
                                    value={betAmount}
                                    onChange={(e) => setBetAmount(Number(e.target.value))}
                                    className="bg-black/20 border-white/10 text-white"
                                  />
                                </div>
                                <Button onClick={placeBet} className="w-full bg-[#FF3B30] hover:bg-[#E63329] text-white font-bold">
                                  Confirm Bet
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="my-bets">
            {myBets.length === 0 ? (
              <div className="text-center py-12">
                <TrendingUp className="w-12 h-12 text-[#52525B] mx-auto mb-4" />
                <p className="text-[#A1A1AA]">You haven't placed any bets yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {myBets.map((bet, idx) => (
                  <div
                    key={idx}
                    className="p-4 rounded-xl bg-[#1A1B1F] border border-white/[0.08] flex items-center justify-between"
                  >
                    <div>
                      <p className="text-white font-medium">{bet.market_id}</p>
                      <p className="text-[#A1A1AA] text-sm">
                        Position: <span className={bet.position === 'yes' ? 'text-[#00E701]' : 'text-[#FF3B30]'}>
                          {bet.position.toUpperCase()}
                        </span>
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-mono">₹{bet.amount}</p>
                      <p className={`text-sm ${
                        bet.status === 'pending' ? 'text-[#FFD600]' : 
                        bet.status === 'won' ? 'text-[#00E701]' : 'text-[#FF3B30]'
                      }`}>
                        {bet.status.toUpperCase()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};
